import { Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, impersonation } from '../../lib/api';
import { Sheet } from '../../ui/Sheet';
import { useParent } from '../ParentContext';
import { PartnerFamiliesSheet } from './AmbassadorFamilies';
import { AmbassadorEmails } from './AmbassadorEmails';

/** Partners and affiliates (#598), as the admin manages them. */
interface PartnerSettings {
  enabled: boolean;
  signupsViaPartner: boolean;
  commissionPct: number;
  commissionMonths: number;
  discountPct: number;
  discountMonths: number;
  holdDays: number;
  minPayoutCents: number;
  applicationsOpen: boolean;
  contactEmail: string;
  stripePayouts: boolean;
  ambassadorMfa: boolean;
  /** Inactivity, Ambassador access and unclaimed earnings (#895). */
  inactivityOn: boolean;
  inactiveMonths: number;
  inactivityNoticeDays: number;
  newAmbassadorGraceDays: number;
  accessAfterEndDays: number;
  unclaimedReminderDays: number;
  unclaimedReportYears: number;
}
/** Where an ambassador stands (#895): their last new family, a warning or ending, unclaimed earnings, their own family. */
interface Standing {
  ambassador: boolean;
  lastFamilyAt: number;
  clockStart: number;
  new: boolean;
  warnAt: number;
  warnedAt: number;
  endsAt: number;
  endedAt: number;
  endedReason: string;
  owedCents: number;
  owedSince: number;
  payoutsReady: boolean;
  reportDue: boolean;
  ownFamily: { planName: string; accessEndsAt: number } | null;
}
/** A Brand Ambassador application from the public page (#713). */
interface Application {
  id: string;
  name: string;
  email: string;
  link: string;
  otherLinks: string;
  kind: string;
  audience: string;
  /** Followers on their largest platform (#902); 0 for older applications. */
  followers: number;
  pitch: string;
  status: 'new' | 'approved' | 'declined';
  createdAt: number;
  suggestedCode: string;
  /** The code they chose when applying (#898), and whether someone else has it now. */
  requestedCode: string;
  requestedTaken: boolean;
  defaultTierId: string;
}
interface Partner {
  id: string;
  name: string;
  kind: 'influencer' | 'business';
  email: string;
  code: string;
  discountPct: number;
  discountMonths: number;
  commissionPct: number;
  commissionMonths: number;
  status: 'active' | 'paused' | 'ended';
  standing: Standing;
  /** Followers (#902), whether you checked them, a number they sent that waits for you, and their tier progress. */
  followers: number;
  followersChecked: boolean;
  followersPending: number | null;
  paidSignups: number;
  progress: Progress;
  /** The ClassGuardian account with this email: it has the Brand Ambassador role (#721). */
  account: { id: string; name: string; email: string; signedIn: boolean } | null;
  /** Payouts through Stripe Connect (#725). */
  stripe: { available: boolean; state: 'none' | 'pending' | 'ready'; bankLast4: string };
  /** '' = Custom (#734). */
  tierId: string;
  /** The plan for their own family: their tier's, or their own. */
  planId: string;
  link: string;
  createdAt: number;
  stats: { families: number; paying: number; waitingCents: number; owedCents: number; paidCents: number; paidThisYearCents: number };
  payouts: { id: string; amountCents: number; method: string; note: string; paidAt: number }[];
}
/** An ambassador tier (#734): terms and a free plan for the ambassador's own family. */
interface Tier {
  id: string;
  name: string;
  commissionPct: number;
  commissionMonths: number;
  discountPct: number;
  discountMonths: number;
  planId: string;
  isDefault: boolean;
  sort: number;
  members: number;
  /** What reaches it (#902): paid sign-ups, or more than this many followers; 0 = not by that. */
  minSignups: number;
  minFollowers: number;
}
/** Where an ambassador is on the ladder (#902). */
interface Progress {
  tier: { id: string; name: string; commissionPct: number; discountPct: number } | null;
  signups: number;
  followers: number;
  next: { name: string; commissionPct: number; signups: number; followers: number; signupsToGo: number } | null;
}
interface PartnersData {
  settings: PartnerSettings;
  partners: Partner[];
  tiers: Tier[];
  freePlans: { id: string; name: string }[];
  totals: { families: number; paying: number; owedCents: number; paidThisYearCents: number };
}

const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const months = (n: number) => (n === 1 ? '1 month' : `${n} months`);
const count = (n: number) => n.toLocaleString();
/** What reaches a tier, in words (#902). */
const reachText = (t: { minSignups: number; minFollowers: number }) =>
  !t.minSignups && !t.minFollowers
    ? 'Where everyone starts'
    : [t.minSignups ? `${count(t.minSignups)} paid sign-ups` : '', t.minFollowers ? `over ${count(t.minFollowers)} followers` : ''].filter(Boolean).join(' or ');
/** What it takes to reach the next tier, in words. */
const nextText = (n: Progress['next']) =>
  !n ? 'Top tier' : `${[n.signupsToGo ? `${n.signupsToGo} more paid sign-ups` : '', n.followers ? `over ${count(n.followers)} followers` : ''].filter(Boolean).join(' or ')} to ${n.name}`;
const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
/** How long ago, roughly: "9 days", "4 months". */
function ago(ms: number) {
  const days = Math.max(0, Math.round((Date.now() - ms) / 86_400_000));
  if (days < 45) return days === 1 ? '1 day ago' : `${days} days ago`;
  const m = Math.round(days / 30.44);
  return m === 1 ? '1 month ago' : `${m} months ago`;
}
/** A Brand Ambassador's place in the program (#895), as a tag. */
function StandingTag({ p }: { p: Partner }) {
  const st = p.standing;
  if (p.status === 'ended') return <span className="tag">Ended{st.endedReason === 'inactive' ? ': inactive' : ''}</span>;
  if (p.status === 'paused') return <span className="tag">Paused</span>;
  if (st.warnedAt) return <span className="tag warn">Warned · ends {day(st.endsAt)}</span>;
  if (st.new) return <span className="tag">New · clock starts {day(st.clockStart)}</span>;
  return <span className="tag good">Active</span>;
}

export function AdminPartners() {
  const { toast } = useParent();
  const [data, setData] = useState<PartnersData | null>(null);
  const [editing, setEditing] = useState<Partner | 'new' | null>(null);
  const [paying, setPaying] = useState<Partner | null>(null);
  const [familiesOf, setFamiliesOf] = useState<Partner | null>(null);
  const [apps, setApps] = useState<Application[]>([]);
  const [reviewing, setReviewing] = useState<Application | null>(null);
  const [tierEditing, setTierEditing] = useState<Tier | 'new' | null>(null);
  const [emailsOpen, setEmailsOpen] = useState(false);
  // Partners and the tier ladder (#902) are two views; #tiers opens the second.
  const [view, setView] = useState<'partners' | 'tiers'>(() => (location.hash === '#tiers' ? 'tiers' : 'partners'));
  const show = (v: 'partners' | 'tiers') => {
    setView(v);
    history.replaceState(null, '', v === 'tiers' ? '#tiers' : location.pathname);
  };
  const [showDecided, setShowDecided] = useState(false);
  const load = useCallback(() => {
    api.get<PartnersData>('/api/admin/partners').then(setData).catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
    api.get<Application[]>('/api/admin/partner-applications').then(setApps).catch(() => undefined);
  }, [toast]);
  useEffect(load, [load]);

  async function saveSettings(patch: Partial<PartnerSettings>) {
    try {
      const settings = await api.put<PartnerSettings>('/api/admin/partners/settings', patch);
      setData((d) => (d ? { ...d, settings } : d));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  /** Restart their inactivity clock today (#895); an ended ambassador is active again. */
  async function keepActive(p: Partner) {
    try {
      await api.post(`/api/admin/partners/${p.id}/keep-active`);
      toast({ text: `${p.name} stays active; their clock starts again today.`, tone: 'good' });
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  if (!data) return <p className="muted">Loading…</p>;
  const s = data.settings;
  // New partners and tiers start from the default tier's rates (#923); the stored defaults only cover having no tiers.
  const baseTier = data.tiers.find((x) => x.isDefault) ?? data.tiers[0];
  const starting = baseTier
    ? { ...s, commissionPct: baseTier.commissionPct, commissionMonths: baseTier.commissionMonths, discountPct: baseTier.discountPct, discountMonths: baseTier.discountMonths }
    : s;
  const fresh = apps.filter((a) => a.status === 'new');
  const decided = apps.filter((a) => a.status !== 'new');
  const ladder = [...data.tiers].sort((a, b) => a.sort - b.sort);
  /** Take or refuse a follower count an ambassador sent (#902). */
  async function followers(p: Partner, pending: 'approve' | 'refuse') {
    try {
      await api.put(`/api/admin/partners/${p.id}/followers`, { pending });
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  return (
    <div className="stack">
      <div className="segmented" role="tablist" aria-label="Partners or tiers">
        <button type="button" role="tab" aria-selected={view === 'partners'} className={view === 'partners' ? 'active' : ''} onClick={() => show('partners')}>
          Partners
        </button>
        <button type="button" role="tab" aria-selected={view === 'tiers'} className={view === 'tiers' ? 'active' : ''} onClick={() => show('tiers')}>
          Tiers
        </button>
      </div>
      {view === 'tiers' && (
        <section className="card stack">
          <div className="row-between wrap">
            <h2>Ambassador tiers</h2>
            <button className="btn small primary" onClick={() => setTierEditing('new')}>
              <Plus size={16} /> Add a tier
            </button>
          </div>
          <p className="muted small">
            Everyone starts on the default tier and moves up on their own when they reach a higher tier’s paid sign-ups or followers (a family counts once it has
            paid). Nobody moves down on their own; move anyone by hand from their sheet. Editing a tier updates everyone on it from then on; nothing already earned
            changes.
          </p>
          {ladder.length === 0 ? (
            <p className="muted">No tiers yet: every ambassador has their own terms.</p>
          ) : (
            <div className="tier-ladder">
              {ladder.map((t) => (
                <div key={t.id} className="tier-card">
                  <div className="row-between">
                    <h3>{t.name}</h3>
                    {t.isDefault && <span className="tag good">Everyone starts here</span>}
                  </div>
                  <div className="tier-big">{t.commissionPct}%</div>
                  <span className="muted small">of what their families pay, for {months(t.commissionMonths)}</span>
                  <span className="tier-line">
                    Families get <strong>{t.discountPct}% off</strong> for {months(t.discountMonths)}
                  </span>
                  <span className="tier-reach">
                    Reached at: <strong>{reachText(t)}</strong>
                  </span>
                  <span className="muted small">Their family: {data.freePlans.find((x) => x.id === t.planId)?.name ?? 'their own choice'}</span>
                  <div className="row-between">
                    <span className="small muted">
                      {t.members} {t.members === 1 ? 'ambassador' : 'ambassadors'}
                    </span>
                    <button className="btn small" onClick={() => setTierEditing(t)}>
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
      {view === 'partners' && (
        <>
      {apps.length > 0 && (
        <section className="card stack">
          <div className="row-between wrap">
            <h2>Brand Ambassador applications</h2>
            {fresh.length > 0 && <span className="tag">{fresh.length} new</span>}
          </div>
          {fresh.length === 0 ? (
            <p className="muted">No new applications.</p>
          ) : (
            <div className="partner-list">
              {fresh.map((a) => (
                <div key={a.id} className="partner-row">
                  <div className="grow">
                    <strong>{a.name}</strong>
                    <span className="muted small block">
                      {a.kind} · {a.followers ? `${count(a.followers)} followers` : a.audience} · applied {new Date(a.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <button className="btn small primary" onClick={() => setReviewing(a)}>
                    Review
                  </button>
                </div>
              ))}
            </div>
          )}
          {decided.length > 0 && (
            <button className="link-btn" onClick={() => setShowDecided(!showDecided)}>
              {showDecided ? 'Hide' : 'Show'} {decided.length} reviewed
            </button>
          )}
          {showDecided &&
            decided.map((a) => (
              <span key={a.id} className="small">
                {a.name} <span className="muted">· {a.status === 'approved' ? 'approved' : 'declined'} · {a.email}</span>
              </span>
            ))}
        </section>
      )}
      <section className="card stack">
        <div className="row-between wrap">
          <h2>Partners</h2>
          <button className="btn primary" onClick={() => setEditing('new')}>
            <Plus size={16} /> Add a partner
          </button>
        </div>
        <p className="muted small">
          Brand ambassadors and businesses share their code or link. Families who sign up through it get the partner’s discount at checkout, and the partner earns a share
          of what those families pay for a time. Earnings are owed {s.holdDays} days after each payment (a refund takes them back first); pay partners outside the app
          and mark it paid here.
        </p>
        <div className="partner-totals">
          <div>
            <span className="muted small">Families from partners</span>
            <strong>{data.totals.families}</strong>
          </div>
          <div>
            <span className="muted small">Paying now</span>
            <strong>{data.totals.paying}</strong>
          </div>
          <div>
            <span className="muted small">Owed to partners</span>
            <strong className={data.totals.owedCents ? 'warn-text' : ''}>{money(data.totals.owedCents)}</strong>
          </div>
          <div>
            <span className="muted small">Paid this year</span>
            <strong>{money(data.totals.paidThisYearCents)}</strong>
          </div>
        </div>
        {data.partners.length === 0 ? (
          <p className="muted">No partners yet.</p>
        ) : (
          <div className="partner-list">
            {data.partners.map((p) => (
              <div key={p.id} className={`partner-row${p.status !== 'active' ? ' paused' : ''}`}>
                <div className="grow">
                  <strong>{p.name}</strong> {p.kind === 'influencer' && <StandingTag p={p} />}
                  <span className="muted small block">
                    {p.status === 'paused' ? 'Paused · code no longer works' : p.status === 'ended' ? 'Ended · code no longer works' : `${p.kind === 'business' ? 'Business' : 'Brand ambassador'} · ${p.commissionPct}% for ${months(p.commissionMonths)}`}
                    {' · '}families {p.discountPct}% off for {months(p.discountMonths)}
                  </span>
                  {p.kind === 'influencer' && (
                    <span className="muted small block">
                      {data.tiers.find((t) => t.id === p.tierId)?.name ?? 'Custom'}
                      {p.planId ? ` · their family’s plan: ${data.freePlans.find((x) => x.id === p.planId)?.name ?? 'a plan'}` : ''}
                    </span>
                  )}
                  {p.account && <span className="muted small block">Account: {p.account.name} · sees this in the app</span>}
                  {p.kind === 'influencer' && p.progress.tier && (
                    <span className="muted small block">
                      {count(p.paidSignups)} paid sign-ups (families who made a first payment) · {count(p.followers)} followers{p.followersChecked ? ' ✓' : ''} · {nextText(p.progress.next)}
                    </span>
                  )}
                  {p.followersPending !== null && (
                    <span className="small block row-gap wrap">
                      <span className="warn-text">Sent a new follower count: {count(p.followersPending)}</span>
                      <button type="button" className="link-btn small" onClick={() => void followers(p, 'approve')}>
                        Approve
                      </button>
                      <button type="button" className="link-btn small" onClick={() => void followers(p, 'refuse')}>
                        Refuse
                      </button>
                    </span>
                  )}
                  {p.kind === 'influencer' && (
                    <span className="muted small block">
                      Last new family: {p.standing.lastFamilyAt ? ago(p.standing.lastFamilyAt) : `none yet · approved ${day(p.createdAt)}`}
                      {p.standing.ownFamily &&
                        ` · own family: ${p.standing.ownFamily.planName || 'no plan'}${p.standing.ownFamily.accessEndsAt ? `, access ends ${day(p.standing.ownFamily.accessEndsAt)}` : ''}`}
                    </span>
                  )}
                  {p.standing.reportDue && (
                    <span className="warn-text small block">
                      Report to state: {money(p.standing.owedCents)} unclaimed since {day(p.standing.owedSince)}
                    </span>
                  )}
                </div>
                <code className="partner-code">{p.code}</code>
                {p.stripe.available && (
                  <span className={`tag ${p.stripe.state === 'ready' ? 'good' : ''}`}>
                    {p.stripe.state === 'ready' ? 'Stripe payouts ready' : p.stripe.state === 'pending' ? 'Stripe checking' : 'No payouts set up'}
                  </span>
                )}
                <button type="button" className="link-btn small partner-families" onClick={() => setFamiliesOf(p)}>
                  {p.stats.families} {p.stats.families === 1 ? 'family' : 'families'}
                  <span className="muted block">{p.stats.paying} paying</span>
                </button>
                <span className={`small ${p.stats.owedCents ? 'warn-text' : 'muted'}`}>
                  {money(p.stats.owedCents)} owed
                  {p.stats.waitingCents > 0 && <span className="muted block">{money(p.stats.waitingCents)} waiting</span>}
                </span>
                <span className="row-gap">
                  {p.stats.owedCents > 0 && (
                    <button className="btn small" onClick={() => setPaying(p)}>
                      Mark paid…
                    </button>
                  )}
                  {p.kind === 'influencer' && (p.standing.warnedAt > 0 || p.status === 'ended') && (
                    <button className="btn small" onClick={() => void keepActive(p)}>
                      Keep active
                    </button>
                  )}
                  <button className="btn small" onClick={() => setEditing(p)}>
                    Open
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card stack">
        <h3>Program settings</h3>
        <label className="check">
          <input type="checkbox" checked={s.enabled} onChange={(e) => void saveSettings({ enabled: e.target.checked })} />
          <span>
            <strong>Partner program on</strong>
            <span className="muted small block">Off: codes and links stop working. Nothing already earned is lost.</span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={s.signupsViaPartner} onChange={(e) => void saveSettings({ signupsViaPartner: e.target.checked })} />
          <span>
            <strong>Families can sign up through a partner’s link while sign-ups are closed</strong>
            <span className="muted small block">Everyone else still sees “Request access”.</span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={s.applicationsOpen} onChange={(e) => void saveSettings({ applicationsOpen: e.target.checked })} />
          <span>
            <strong>Take Brand Ambassador applications</strong>
            <span className="muted small block">
              Anyone can apply on the public <a href="/ambassadors" target="_blank" rel="noreferrer">Brand Ambassadors page</a>; you’re emailed and they appear above for review. Off: the page says applications are closed.
            </span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={s.stripePayouts} onChange={(e) => void saveSettings({ stripePayouts: e.target.checked })} />
          <span>
            <strong>Pay ambassadors with Stripe</strong>
            <span className="muted small block">
              Ambassadors set up payouts on Stripe’s pages (bank and tax ID; you never see them), you send what’s owed from your Stripe balance, and Stripe
              files their 1099-NECs once tax reporting is on in your Stripe Dashboard (Connect → Tax forms). Needs Connect turned on in Stripe.
            </span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={s.ambassadorMfa} onChange={(e) => void saveSettings({ ambassadorMfa: e.target.checked })} />
          <span>
            <strong>Ambassadors must use two-factor sign-in</strong>
            <span className="muted small block">Brand Ambassadors sign in with their ClassGuardian account; with this on, it must have two-factor (an authenticator app or a passkey) before anything opens.</span>
          </span>
        </label>
        <label>
          Ambassador contact email
          <input
            type="email"
            defaultValue={s.contactEmail}
            placeholder="Blank = the support email"
            maxLength={200}
            onBlur={(e) => e.target.value.trim() !== s.contactEmail && void saveSettings({ contactEmail: e.target.value.trim() })}
          />
          <span className="muted small block">Shown on the Brand Ambassador terms and each ambassador’s page, and at the end of every email ambassadors get from us.</span>
        </label>
        {/* Rates live on the tiers now (#923); a new business or Custom partner starts from the default tier. */}
        <h4>Payouts</h4>
        <div className="partner-defaults">
          <NumberField label="Owed after (days)" value={s.holdDays} max={120} onSave={(v) => void saveSettings({ holdDays: v })} />
          <NumberField label="Pay out from ($)" value={s.minPayoutCents / 100} max={10000} onSave={(v) => void saveSettings({ minPayoutCents: Math.round(v * 100) })} />
        </div>
        <p className="muted small">For every partner: earnings are owed this many days after each payment, and paid out once they reach this amount. Rates live on the tiers; a new business partner or Custom ambassador starts from the default tier and can be changed on their own terms.</p>

        <h4>Inactivity</h4>
        <label className="check">
          <input type="checkbox" checked={s.inactivityOn} onChange={(e) => void saveSettings({ inactivityOn: e.target.checked })} />
          <span>
            <strong>End inactive ambassadors automatically</strong>
            <span className="muted small block">
              When no new family signs up with a Brand Ambassador’s code for the months below, they’re emailed a warning; if still none by the end of the
              notice, their part ends. Off: the list still shows when each one last brought a family, but nobody is warned or ended.
            </span>
          </span>
        </label>
        <div className="partner-defaults">
          <NumberField label="Inactive after (months)" value={s.inactiveMonths} max={60} onSave={(v) => void saveSettings({ inactiveMonths: v })} />
          <NumberField label="Warning, then end after (days)" value={s.inactivityNoticeDays} max={180} onSave={(v) => void saveSettings({ inactivityNoticeDays: v })} />
          <NumberField label="New ambassadors’ head start (days)" value={s.newAmbassadorGraceDays} max={365} onSave={(v) => void saveSettings({ newAmbassadorGraceDays: v })} />
        </div>

        <h4>Their own family’s plan</h4>
        <div className="partner-defaults">
          <NumberField label="Access after their part ends (days)" value={s.accessAfterEndDays} max={365} onSave={(v) => void saveSettings({ accessAfterEndDays: v })} />
        </div>
        <p className="muted small">
          Ambassadors’ families are on an Ambassador access plan: no charge while they’re active, not free for life. When their part ends (inactive, paused or
          removed) the family keeps it for these days, then chooses a plan. Making them active again puts the family back on it.
        </p>

        <h4>Unclaimed earnings</h4>
        <div className="partner-defaults">
          <NumberField label="Remind every (days, 0 = never)" value={s.unclaimedReminderDays} max={365} onSave={(v) => void saveSettings({ unclaimedReminderDays: v })} />
          <NumberField label="Flag for state reporting after (years)" value={s.unclaimedReportYears} max={10} onSave={(v) => void saveSettings({ unclaimedReportYears: v })} />
        </div>
        <p className="muted small">
          Reminders go while money is owed and their Stripe payouts aren’t set up. Earnings owed longer than the years above show “Report to state” in the list;
          nothing is sent to a state automatically. The right period depends on the ambassador’s state, so check it with your accountant.
        </p>
        <button className="btn" onClick={() => setEmailsOpen(true)}>
          Ambassador emails…
        </button>
      </section>

        </>
      )}
      {editing && <PartnerSheet partner={editing === 'new' ? null : editing} defaults={starting} tiers={data.tiers} plans={data.freePlans} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), load())} />}
      {reviewing && <ReviewSheet app={reviewing} defaults={starting} tiers={data.tiers} onClose={() => setReviewing(null)} onDone={() => (setReviewing(null), load())} />}
      {tierEditing && <TierSheet tier={tierEditing === 'new' ? null : tierEditing} plans={data.freePlans} defaults={starting} onClose={() => setTierEditing(null)} onSaved={() => (setTierEditing(null), load())} />}
      {emailsOpen && <AmbassadorEmails onClose={() => setEmailsOpen(false)} />}
      {familiesOf && <PartnerFamiliesSheet partner={familiesOf} onClose={() => setFamiliesOf(null)} />}
      {paying && <PayoutSheet partner={paying} minCents={s.minPayoutCents} onClose={() => setPaying(null)} onPaid={() => (setPaying(null), load())} />}
    </div>
  );
}

function NumberField({ label, value, max, onSave }: { label: string; value: number; max: number; onSave: (v: number) => void }) {
  return (
    <label>
      {label}
      <input type="number" min={0} max={max} defaultValue={value} onBlur={(e) => Number(e.target.value) !== value && onSave(Number(e.target.value))} />
    </label>
  );
}

/** Add or change a partner: who they are, their code, the family's discount and what they earn. */
/** One application: who they are and where they share; approve with a code and terms, or decline (#713). */
function ReviewSheet({ app, defaults, tiers, onClose, onDone }: { app: Application; defaults: PartnerSettings; tiers: Tier[]; onClose: () => void; onDone: () => void }) {
  const { toast } = useParent();
  const first = tiers.find((x) => x.id === app.defaultTierId);
  const [t, setT] = useState({
    code: app.suggestedCode,
    tierId: first?.id ?? '',
    discountPct: first?.discountPct ?? defaults.discountPct,
    discountMonths: first?.discountMonths ?? defaults.discountMonths,
    commissionPct: first?.commissionPct ?? defaults.commissionPct,
    commissionMonths: first?.commissionMonths ?? defaults.commissionMonths,
  });
  const onTier = (id: string) => {
    const x = tiers.find((y) => y.id === id);
    setT(x ? { ...t, tierId: x.id, discountPct: x.discountPct, discountMonths: x.discountMonths, commissionPct: x.commissionPct, commissionMonths: x.commissionMonths } : { ...t, tierId: '' });
  };
  const [tell, setTell] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ accountCreated: boolean; inviteUrl: string; emailed: boolean } | null>(null);
  const num = (k: keyof typeof t, v: string) => setT({ ...t, [k]: Math.max(0, parseInt(v, 10) || 0) });
  const href = (u: string) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

  async function approve() {
    setBusy(true);
    try {
      setDone(await api.post<{ accountCreated: boolean; inviteUrl: string; emailed: boolean }>(`/api/admin/partner-applications/${app.id}/approve`, t));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  async function decline() {
    if (!confirm(`Decline ${app.name}’s application?`)) return;
    setBusy(true);
    try {
      await api.post(`/api/admin/partner-applications/${app.id}/decline`, { tell });
      toast({ text: `${app.name}’s application was declined.`, tone: 'good' });
      onDone();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
      setBusy(false);
    }
  }

  return (
    <Sheet title={app.name} onClose={done ? onDone : onClose}>
      {done ? (
        <div className="stack">
          <p>
            <strong>{app.name}</strong> is a Brand Ambassador with the code <code className="partner-code">{t.code.toUpperCase()}</code>.
          </p>
          <p className="muted">
            {done.emailed
              ? `We emailed ${app.email} their code and link${done.accountCreated ? ', and a link to choose the password for their new ClassGuardian account' : ''}. They sign in like anyone else and open the Ambassador tab.`
              : done.accountCreated
                ? `Email isn’t set up, so send ${app.name} this link to choose their password:`
                : `Email isn’t set up. They already have an account: they sign in and open the Ambassador tab.`}
          </p>
          {!done.emailed && done.inviteUrl && <code className="break">{done.inviteUrl}</code>}
          <button className="btn primary" onClick={onDone}>
            Done
          </button>
        </div>
      ) : (
        <div className="stack">
          <span className="muted">
            <a href={`mailto:${app.email}`}>{app.email}</a> · {app.kind} · {app.audience}
          </span>
          <a href={href(app.link)} target="_blank" rel="noreferrer noopener" className="break">
            {app.link}
          </a>
          {app.otherLinks && <span className="small break">Also: {app.otherLinks}</span>}
          {app.pitch && <blockquote className="amb-pitch-quote">{app.pitch}</blockquote>}
          {tiers.length > 0 && (
            <label>
              Tier
              <select value={t.tierId} onChange={(e) => onTier(e.target.value)}>
                {tiers.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                    {x.isDefault ? ' (default)' : ''}
                  </option>
                ))}
                <option value="">Custom</option>
              </select>
            </label>
          )}
          {app.requestedCode && (
            <p className={`small ${app.requestedTaken ? 'warn-text' : 'muted'}`}>
              {app.requestedTaken
                ? `They asked for ${app.requestedCode}, but it’s been taken since. ${app.suggestedCode} is filled in instead; change it if you like, and tell them.`
                : `They chose ${app.requestedCode}. You can change it before approving.`}
            </p>
          )}
          <div className="field-row wrap-row">
            <label>
              Code
              <input value={t.code} onChange={(e) => setT({ ...t, code: e.target.value })} maxLength={16} />
            </label>
            <label>
              They earn (%)
              <input type="number" min={0} max={100} value={t.commissionPct} disabled={!!t.tierId} onChange={(e) => num('commissionPct', e.target.value)} />
            </label>
            <label>
              for (months)
              <input type="number" min={0} max={120} value={t.commissionMonths} disabled={!!t.tierId} onChange={(e) => num('commissionMonths', e.target.value)} />
            </label>
            <label>
              Families get (% off)
              <input type="number" min={0} max={100} value={t.discountPct} disabled={!!t.tierId} onChange={(e) => num('discountPct', e.target.value)} />
            </label>
            <label>
              for (months)
              <input type="number" min={0} max={120} value={t.discountMonths} disabled={!!t.tierId} onChange={(e) => num('discountMonths', e.target.value)} />
            </label>
          </div>
          <p className="muted small">Approving makes them a partner with this code and emails them their link and private partner page.</p>
          <div className="row-gap wrap">
            <button className="btn primary" disabled={busy || t.code.trim().length < 3} onClick={() => void approve()}>
              Approve and send their page
            </button>
            <button className="btn" disabled={busy} onClick={() => void decline()}>
              Decline
            </button>
            <label className="check small">
              <input type="checkbox" checked={tell} onChange={(e) => setTell(e.target.checked)} />
              <span>Email them if I decline</span>
            </label>
          </div>
        </div>
      )}
    </Sheet>
  );
}

/** Add or edit an ambassador tier (#734): its terms, its free plan, and whether new ambassadors start on it. */
function TierSheet({
  tier,
  plans,
  defaults,
  onClose,
  onSaved,
}: {
  tier: Tier | null;
  plans: { id: string; name: string }[];
  defaults: PartnerSettings;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useParent();
  const [f, setF] = useState({
    name: tier?.name ?? '',
    commissionPct: tier?.commissionPct ?? defaults.commissionPct,
    commissionMonths: tier?.commissionMonths ?? defaults.commissionMonths,
    discountPct: tier?.discountPct ?? defaults.discountPct,
    discountMonths: tier?.discountMonths ?? defaults.discountMonths,
    planId: tier?.planId ?? '',
    isDefault: tier?.isDefault ?? false,
    sort: tier?.sort ?? 0,
    minSignups: tier?.minSignups ?? 0,
    minFollowers: tier?.minFollowers ?? 0,
  });
  const [busy, setBusy] = useState(false);
  const num = (k: 'commissionPct' | 'commissionMonths' | 'discountPct' | 'discountMonths' | 'minSignups' | 'minFollowers' | 'sort', v: string) =>
    setF({ ...f, [k]: Math.max(0, parseInt(v, 10) || 0) });
  async function save() {
    setBusy(true);
    try {
      if (tier) await api.put(`/api/admin/partner-tiers/${tier.id}`, f);
      else await api.post('/api/admin/partner-tiers', f);
      toast({ text: `${f.name} saved.`, tone: 'good' });
      onSaved();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    if (!tier) return;
    if (!confirm(`Delete the ${tier.name} tier? ${tier.members ? `Its ${tier.members} ambassador${tier.members === 1 ? '' : 's'} keep the same terms and plan, as Custom.` : ''}`)) return;
    setBusy(true);
    try {
      await api.del(`/api/admin/partner-tiers/${tier.id}`);
      toast({ text: `Deleted ${tier.name}.`, tone: 'good' });
      onSaved();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
      setBusy(false);
    }
  }
  return (
    <Sheet title={tier ? `${tier.name} tier` : 'Add a tier'} onClose={onClose}>
      <div className="stack">
        <label>
          Name
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={40} placeholder="Gold" />
        </label>
        <fieldset className="partner-terms">
          <legend>Ambassadors earn</legend>
          <span className="row-gap wrap">
            <input type="number" min={0} max={100} value={f.commissionPct} onChange={(e) => num('commissionPct', e.target.value)} aria-label="Commission percent" /> % of each payment for
            <input type="number" min={0} max={120} value={f.commissionMonths} onChange={(e) => num('commissionMonths', e.target.value)} aria-label="Commission months" /> months
          </span>
        </fieldset>
        <fieldset className="partner-terms">
          <legend>Their families get</legend>
          <span className="row-gap wrap">
            <input type="number" min={0} max={100} value={f.discountPct} onChange={(e) => num('discountPct', e.target.value)} aria-label="Discount percent" /> % off for
            <input type="number" min={0} max={120} value={f.discountMonths} onChange={(e) => num('discountMonths', e.target.value)} aria-label="Discount months" /> months
          </span>
        </fieldset>
        <fieldset className="partner-terms">
          <legend>Reached at</legend>
          <span className="row-gap wrap">
            <input type="number" min={0} value={f.minSignups} onChange={(e) => num('minSignups', e.target.value)} aria-label="Paid sign-ups" /> paid sign-ups, or over
            <input type="number" min={0} value={f.minFollowers} onChange={(e) => num('minFollowers', e.target.value)} aria-label="Followers" /> followers
          </span>
          <span className="muted small">A sign-up counts only once that family makes its first payment. 0 for both: nobody moves up into it on their own (use it for the starting tier, or one you give by hand).</span>
          <label>
            Order on the ladder
            <input type="number" min={0} value={f.sort} onChange={(e) => num('sort', e.target.value)} />
            <span className="muted small block">Lowest first. Ambassadors only move up to a tier further along.</span>
          </label>
        </fieldset>
        <label>
          Plan for the ambassador’s own family
          <select value={f.planId} onChange={(e) => setF({ ...f, planId: e.target.value })}>
            <option value="">None (their own choice)</option>
            {plans.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
          <span className="muted small block">Free plans only. Their family is put on it once they have a ClassGuardian account with their email.</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={f.isDefault} onChange={(e) => setF({ ...f, isDefault: e.target.checked })} />
          <span>
            Default tier
            <span className="muted small block">Newly approved ambassadors start here.</span>
          </span>
        </label>
        {tier && tier.members > 0 && (
          <p className="muted small">
            Saving updates its {tier.members} ambassador{tier.members === 1 ? '' : 's'} from now on; nothing already earned changes.
          </p>
        )}
        <span className="row-gap wrap">
          <button className="btn primary" disabled={busy || !f.name.trim()} onClick={() => void save()}>
            {tier ? 'Save' : 'Add tier'}
          </button>
          {tier && (
            <button className="btn danger-text" disabled={busy} onClick={() => void remove()}>
              Delete tier
            </button>
          )}
        </span>
      </div>
    </Sheet>
  );
}

/**
 * Delete an ambassador or partner (#738): out of the program, with what was paid kept in Finances; and, if asked, their
 * ClassGuardian account and its family data too (the same as deleting it in Admin → Families).
 */
function DeletePartner({ partner, onClose, onDone }: { partner: Partner; onClose: () => void; onDone: () => void }) {
  const { toast } = useParent();
  const [alsoAccount, setAlsoAccount] = useState(false);
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      await api.del(`/api/admin/partners/${partner.id}`);
      if (alsoAccount && partner.account) await api.del(`/api/admin/parents/${partner.account.id}`);
      toast({ text: `Deleted ${partner.name}${alsoAccount && partner.account ? ' and their account' : ''}.`, tone: 'good' });
      onDone();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
      setBusy(false);
    }
  }
  return (
    <Sheet title={`Delete ${partner.name}?`} onClose={onClose}>
      <div className="stack">
        <p>
          Their code <code className="partner-code">{partner.code}</code> stops working, and families they brought are no longer counted as theirs. What
          they were paid stays in Finances, with their name.
        </p>
        {partner.stats.owedCents + partner.stats.waitingCents > 0 && (
          <p className="warn-text">
            They haven’t been paid {money(partner.stats.owedCents + partner.stats.waitingCents)} they earned. Deleting them drops it.
          </p>
        )}
        {partner.stripe.state !== 'none' && <p className="muted small">Their Stripe account stays in your Stripe dashboard; close it there if you want.</p>}
        {partner.account && (
          <label className="check">
            <input type="checkbox" checked={alsoAccount} onChange={(e) => setAlsoAccount(e.target.checked)} />
            <span>
              Also delete their ClassGuardian account ({partner.account.email})
              <span className="muted small block">With its family, children and everything in it. This can’t be undone.</span>
            </span>
          </label>
        )}
        <span className="row-gap wrap">
          <button className="btn danger-text" disabled={busy} onClick={() => void go()}>
            Delete {partner.name}
          </button>
          <button className="btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
        </span>
      </div>
    </Sheet>
  );
}

function PartnerSheet({
  partner,
  defaults,
  tiers,
  plans,
  onClose,
  onSaved,
}: {
  partner: Partner | null;
  defaults: PartnerSettings;
  tiers: Tier[];
  plans: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useParent();
  const [f, setF] = useState({
    name: partner?.name ?? '',
    kind: partner?.kind ?? ('influencer' as const),
    email: partner?.email ?? '',
    code: partner?.code ?? '',
    discountPct: partner?.discountPct ?? defaults.discountPct,
    discountMonths: partner?.discountMonths ?? defaults.discountMonths,
    commissionPct: partner?.commissionPct ?? defaults.commissionPct,
    commissionMonths: partner?.commissionMonths ?? defaults.commissionMonths,
    // A new ambassador starts on the default tier (#734); '' = Custom.
    tierId: partner ? partner.tierId : (tiers.find((x) => x.isDefault)?.id ?? ''),
    planId: partner?.planId ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const tier = f.kind === 'influencer' ? tiers.find((x) => x.id === f.tierId) : undefined;
  // What's shown and saved: the tier's terms and plan while on one.
  const shown = tier
    ? { ...f, discountPct: tier.discountPct, discountMonths: tier.discountMonths, commissionPct: tier.commissionPct, commissionMonths: tier.commissionMonths, planId: tier.planId }
    : f;
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF({ ...f, [k]: v });

  async function save(status?: 'active' | 'paused') {
    setBusy(true);
    try {
      const body = { ...shown, tierId: tier ? tier.id : '', planId: tier ? undefined : f.planId };
      if (partner) await api.put(`/api/admin/partners/${partner.id}`, status ? { status } : body);
      else await api.post('/api/admin/partners', body);
      toast({ text: status ? (status === 'paused' ? `${f.name} is paused.` : `${f.name} is active again.`) : `${f.name} saved.`, tone: 'good' });
      onSaved();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  /** Make their ClassGuardian account and email them the set-up (#736), or email a new sign-in link (#739). */
  const [deleting, setDeleting] = useState(false);
  /** See ClassGuardian as they do (#741), straight to their Brand Ambassador page. Audited, like View as in Families. */
  async function viewAs() {
    if (!partner?.account) return;
    if (!confirm(`View ClassGuardian as ${partner.account.name} (${partner.account.email})? This is recorded in the audit log.`)) return;
    try {
      const r = await api.post<{ token: string; parent: { name: string } }>(`/api/admin/impersonate/${partner.account.id}`);
      impersonation.start(r.token, r.parent.name);
      location.assign('/ambassador');
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  async function setUpAccount() {
    if (!partner) return;
    try {
      const r = await api.post<{ created: boolean; emailed: boolean; inviteUrl: string }>(`/api/admin/partners/${partner.id}/account`);
      if (!r.emailed) setInviteUrl(r.inviteUrl);
      toast({
        text: r.emailed
          ? r.created
            ? `Made ${partner.name}’s account and emailed them the set-up.`
            : `Emailed ${partner.name} a new sign-in link.`
          : `Email isn’t set up. Send ${partner.name} the link below.`,
        tone: 'good',
      });
      if (r.emailed) onSaved();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  const copy = (text: string) => navigator.clipboard.writeText(text).then(() => toast({ text: 'Copied.', tone: 'good' }), () => undefined);

  return (
    <Sheet title={partner ? partner.name : 'Add a partner'} onClose={onClose}>
      <div className="stack">
        <div className="field-row wrap-row">
          <label className="grow">
            Name
            <input value={f.name} onChange={(e) => set('name', e.target.value)} maxLength={80} />
          </label>
          <label>
            Kind
            <select value={f.kind} onChange={(e) => set('kind', e.target.value as 'influencer' | 'business')}>
              <option value="influencer">Brand ambassador</option>
              <option value="business">Business</option>
            </select>
          </label>
        </div>
        <div className="field-row wrap-row">
          <label className="grow">
            Contact email
            <input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} maxLength={200} />
          </label>
          <label>
            Code
            <input value={f.code} onChange={(e) => set('code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} maxLength={16} placeholder="SUNNYDAYS" />
          </label>
        </div>
        {f.kind === 'influencer' && tiers.length > 0 && (
          <label>
            Tier
            <select value={tier ? tier.id : ''} onChange={(e) => set('tierId', e.target.value)}>
              {tiers.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                  {x.isDefault ? ' (default)' : ''}
                </option>
              ))}
              <option value="">Custom</option>
            </select>
            <span className="muted small block">{tier ? `From the ${tier.name} tier. Pick Custom to set their own terms and plan.` : 'Their own terms and plan.'}</span>
          </label>
        )}
        <fieldset className="partner-terms">
          <legend>The family’s discount</legend>
          <span className="row-gap wrap">
            <input type="number" min={0} max={100} value={shown.discountPct} disabled={!!tier} onChange={(e) => set('discountPct', Number(e.target.value))} aria-label="Discount percent" /> % off for
            <input type="number" min={0} max={120} value={shown.discountMonths} disabled={!!tier} onChange={(e) => set('discountMonths', Number(e.target.value))} aria-label="Discount months" /> months
          </span>
        </fieldset>
        <fieldset className="partner-terms">
          <legend>The partner earns</legend>
          <span className="row-gap wrap">
            <input type="number" min={0} max={100} value={shown.commissionPct} disabled={!!tier} onChange={(e) => set('commissionPct', Number(e.target.value))} aria-label="Commission percent" /> % of each payment for
            <input type="number" min={0} max={120} value={shown.commissionMonths} disabled={!!tier} onChange={(e) => set('commissionMonths', Number(e.target.value))} aria-label="Commission months" /> months
          </span>
          <span className="muted small">
            Owed {defaults.holdDays} days after each payment · paid out from {money(defaults.minPayoutCents)} · changes apply to families who sign up from now on
          </span>
        </fieldset>
        {partner && partner.kind === 'influencer' && <FollowersField partner={partner} onSaved={onSaved} />}
        {f.kind === 'influencer' && (
          <label>
            Plan for their own family
            <select value={shown.planId} disabled={!!tier} onChange={(e) => set('planId', e.target.value)}>
              <option value="">None (their own choice)</option>
              {plans.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
            <span className="muted small block">
              {partner?.account
                ? `Their account (${partner.account.email}) is put on it, and moves with the tier or plan.`
                : 'Put on their family once they have a ClassGuardian account with this email. Free plans only.'}
            </span>
          </label>
        )}
        {partner && (
          <div className="stack tight small">
            <strong>Share link</strong>
            <span className="row-gap wrap">
              <code>{partner.link}</code>
              <button type="button" className="link-btn small" onClick={() => copy(partner.link)}>
                Copy
              </button>
            </span>
            <strong>Their ClassGuardian account</strong>
            {partner.account && !inviteUrl ? (
              <span className="row-gap wrap">
                <span className="muted">
                  {partner.account.name} ({partner.account.email}){partner.account.signedIn ? '' : ', hasn’t signed in yet'}: they sign in and open the Ambassador tab.
                </span>
                <button type="button" className="link-btn small" onClick={() => void setUpAccount()}>
                  Email a new sign-in link
                </button>
                <button type="button" className="link-btn small" onClick={() => void viewAs()}>
                  View as {partner.name.split(' ')[0]}
                </button>
              </span>
            ) : inviteUrl ? (
              <span className="row-gap wrap">
                <code className="break">{inviteUrl}</code>
                <button type="button" className="link-btn small" onClick={() => copy(inviteUrl)}>
                  Copy
                </button>
              </span>
            ) : (
              <span className="row-gap wrap">
                <span className="muted">No account with {partner.email || 'their email'} yet.</span>
                {partner.email && (
                  <button type="button" className="link-btn small" onClick={() => void setUpAccount()}>
                    Make their account and email the set-up
                  </button>
                )}
              </span>
            )}
            <button type="button" className="link-btn small danger-text" onClick={() => setDeleting(true)}>
              Delete {partner.kind === 'influencer' ? 'ambassador' : 'partner'}…
            </button>
            {deleting && <DeletePartner partner={partner} onClose={() => setDeleting(false)} onDone={onSaved} />}
            {partner.payouts.length > 0 && (
              <>
                <strong>Paid</strong>
                {partner.payouts.map((p) => (
                  <span key={p.id} className="muted">
                    {new Date(p.paidAt).toLocaleDateString()} · {money(p.amountCents)}
                    {p.method ? ` · ${p.method}` : ''}
                    {p.note ? ` · ${p.note}` : ''}
                  </span>
                ))}
                <span className="muted">This year: {money(partner.stats.paidThisYearCents)} (for a 1099, check with your accountant)</span>
              </>
            )}
          </div>
        )}
        <div className="row-gap wrap">
          <button className="btn primary" disabled={busy || !f.name.trim() || f.code.length < 3} onClick={() => void save()}>
            {partner ? 'Save' : 'Add partner'}
          </button>
          {partner && (
            <button className="btn danger-text" disabled={busy} onClick={() => void save(partner.status !== 'active' ? 'active' : 'paused')}>
              {partner.status !== 'active' ? 'Make active again' : 'Pause partner'}
            </button>
          )}
        </div>
      </div>
    </Sheet>
  );
}

/** Their followers (#902): the number that counts toward the tiers, and whether you checked it. */
function FollowersField({ partner, onSaved }: { partner: Partner; onSaved: () => void }) {
  const { toast } = useParent();
  const [n, setN] = useState(String(partner.followers));
  const [checked, setChecked] = useState(partner.followersChecked);
  async function save() {
    try {
      await api.put(`/api/admin/partners/${partner.id}/followers`, { followers: Math.max(0, parseInt(n, 10) || 0), checked });
      toast({ text: 'Followers saved.', tone: 'good' });
      onSaved();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  return (
    <fieldset className="partner-terms">
      <legend>Followers</legend>
      <span className="row-gap wrap">
        <input type="number" min={0} value={n} onChange={(e) => setN(e.target.value)} aria-label="Followers" />
        <label className="check">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} /> I checked this
        </label>
        <button type="button" className="btn small" onClick={() => void save()} disabled={n === String(partner.followers) && checked === partner.followersChecked}>
          Save followers
        </button>
      </span>
      <span className="muted small">
        Their largest platform. {count(partner.paidSignups)} paid sign-ups · {nextText(partner.progress.next)}.
      </span>
    </fieldset>
  );
}

/** Record a payment made outside the app: everything owed now is marked paid. */
function PayoutSheet({ partner, minCents, onClose, onPaid }: { partner: Partner; minCents: number; onClose: () => void; onPaid: () => void }) {
  const { toast } = useParent();
  const [method, setMethod] = useState('PayPal');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  async function sendWithStripe() {
    setBusy(true);
    try {
      await api.post(`/api/admin/partners/${partner.id}/stripe-payout`);
      toast({ text: `Sent ${money(partner.stats.owedCents)} to ${partner.name} with Stripe.`, tone: 'good' });
      onPaid();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  const viaStripe = partner.stripe.available;
  const [other, setOther] = useState(!viaStripe);
  async function pay() {
    setBusy(true);
    try {
      await api.post(`/api/admin/partners/${partner.id}/payouts`, { method, note });
      toast({ text: `Marked ${money(partner.stats.owedCents)} paid to ${partner.name}.`, tone: 'good' });
      onPaid();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Sheet title={`Pay ${partner.name}`} onClose={onClose}>
      <div className="stack">
        <p>
          Owed now: <strong>{money(partner.stats.owedCents)}</strong>
          {partner.stats.owedCents < minCents && <span className="warn-text small block">That’s under your {money(minCents)} minimum. You can still pay it.</span>}
        </p>
        {viaStripe && partner.stripe.state === 'ready' && (
          <>
            <p className="muted small">Sent from your Stripe balance to {partner.name}’s Stripe account; it reaches their bank in a few days.</p>
            <button className="btn primary" disabled={busy} onClick={() => void sendWithStripe()}>
              Send {money(partner.stats.owedCents)} with Stripe
            </button>
          </>
        )}
        {viaStripe && partner.stripe.state !== 'ready' && (
          <p className="warn-text small">
            {partner.name} {partner.stripe.state === 'pending' ? 'hasn’t finished' : 'hasn’t'} set up payouts with Stripe yet, so Stripe can’t pay them or file their 1099.
          </p>
        )}
        {viaStripe && !other && (
          <button className="link-btn" onClick={() => setOther(true)}>
            Mark paid another way…
          </button>
        )}
        {other && (
          <>
        <p className="muted small">Pay them yourself first, then record it here. Everything owed now is marked paid.{viaStripe ? ' Payments outside Stripe aren’t on Stripe’s 1099s; Finances lists them for your accountant.' : ''}</p>
        <label>
          Paid by
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option>PayPal</option>
            <option>Venmo</option>
            <option>Check</option>
            <option>Bank transfer</option>
            <option>Account credit</option>
            <option>Other</option>
          </select>
        </label>
        <label>
          Note <span className="muted small">(optional)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
        </label>
        <button className={`btn ${viaStripe ? '' : 'primary'}`} disabled={busy} onClick={() => void pay()}>
          Mark {money(partner.stats.owedCents)} paid
        </button>
          </>
        )}
      </div>
    </Sheet>
  );
}
